/**
 * RootLayout — used for superadmin role only.
 * Extends the staff sidebar with a visually distinct "ADMIN" section at the bottom — every
 * superadmin feature lives there, one entry per area.
 * StaffLayout.tsx is NOT modified — this is a parallel layout.
 */

import React, { useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Paintbrush,
  Palette,
  LayoutTemplate,
  Plug,
  CreditCard,
  Bell,
  ChevronDown,
  LogOut,
  X,
  Menu,
  Users,
  Globe,
  Wallet,
  Layers,
  Receipt,
  Activity,
  Database,
  FileText,
  Sparkles,
  Bot,
  MessageSquareWarning,
  MessageSquarePlus,
  ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// ── Regular staff nav items (mirrors StaffLayout navItems) ────────────────────

const staffNavItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/studio', label: 'Site Builder', icon: Paintbrush },
  { to: '/brands', label: 'Brand Manager', icon: Palette },
  { to: '/templates', label: 'Templates', icon: LayoutTemplate },
  { to: '/integrations', label: 'Integrations', icon: Plug },
  { to: '/autopilot', label: 'SEO Autopilot', icon: Bot },
  { to: '/billing', label: 'Billing', icon: CreditCard },
];

// ── Admin nav items — every superadmin feature, one entry per area ─────────────
//
// An area with several pages (Payments, Websites, Templates) shows them as tabs on the page
// (components/AdminNav.tsx). Ordered by how often each is opened: health first, then the
// people and money you act on, then the catalogue you maintain, then reference data.

const rootNavItems = [
  { to: '/admin/system-status', label: 'System status', icon: Activity },
  { to: '/root/users', label: 'Accounts', icon: Users },
  { to: '/root/websites', label: 'Websites', icon: Globe, match: ['/admin/sites'] },
  { to: '/root/requests', label: 'Requests', icon: MessageSquarePlus },
  { to: '/admin/payments', label: 'Payments', icon: Receipt, match: ['/root/payments', '/root/wallets', '/root/plans'] },
  { to: '/root/templates', label: 'Templates', icon: LayoutTemplate, match: ['/admin/templates'] },
  { to: '/root/site-looks', label: 'Site Looks', icon: Layers },
  { to: '/admin/mls-catalog', label: 'MLS catalog', icon: Database },
  { to: '/admin/legal-documents', label: 'Legal documents', icon: FileText },
  { to: '/admin/ai-spend', label: 'AI spend', icon: Sparkles },
  { to: '/admin/chat-dead-ends', label: 'Chat dead ends', icon: MessageSquareWarning },
];

// ── Sidebar ────────────────────────────────────────────────────────────────────

/** An area's sidebar entry lights up on any of the routes its tabs serve. */
export function areaActive(pathname: string, match?: readonly string[]): boolean {
  return !!match?.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function RootSidebar({ onClose }: { onClose?: () => void }) {
  const { pathname } = useLocation();
  return (
    <div
      className="flex h-full flex-col border-r"
      style={{ backgroundColor: '#10032A' }}
    >
      {/* Header */}
      <div
        className="flex h-14 items-center justify-between px-4"
        style={{ borderBottom: '1px solid #281C3F' }}
      >
        <div className="flex items-center gap-2">
          <div
            className="flex h-7 w-7 items-center justify-center rounded text-sm font-bold"
            style={{ backgroundColor: '#4E1F60', color: '#C9B8F4', fontFamily: 'Montserrat, sans-serif' }}
          >
            BL
          </div>
          <span
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              color: '#FFFFFF',
            }}
          >
            Bread List Sites
          </span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded-md p-1 lg:hidden"
            style={{ color: '#584F6A' }}
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-3 space-y-1">
          {staffNavItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-plum text-brand-lavender'
                    : 'text-[#888195] hover:text-white hover:bg-brand-plum-deep'
                )
              }
              style={({ isActive }) =>
                isActive
                  ? { fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }
                  : { fontFamily: 'Montserrat, sans-serif' }
              }
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {label}
            </NavLink>
          ))}
        </div>

        {/* ── ADMIN section separator ── */}
        <div
          style={{
            margin: '16px 12px 8px',
            borderTop: '1px solid #403555',
            paddingTop: 12,
          }}
        >
          <p
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600,
              fontSize: 10,
              color: '#584F6A',
              textTransform: 'lowercase',
              letterSpacing: '0.12em',
              paddingLeft: 12,
              marginBottom: 4,
              fontVariant: 'small-caps',
            }}
          >
            admin
          </p>
          <div
            className="space-y-1"
            style={{ backgroundColor: '#0d0220', borderRadius: 8, padding: '4px 0' }}
          >
            {rootNavItems.map(({ to, label, icon: Icon, match }) => (
              <NavLink
                key={to}
                to={to}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors rounded-md mx-1',
                    isActive || areaActive(pathname, match)
                      ? 'bg-brand-plum text-brand-lavender'
                      : 'text-[#888195] hover:text-white hover:bg-brand-plum-deep'
                  )
                }
                style={({ isActive }) =>
                  isActive || areaActive(pathname, match)
                    ? { fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }
                    : { fontFamily: 'Montserrat, sans-serif' }
                }
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>
    </div>
  );
}

// ── Top bar ────────────────────────────────────────────────────────────────────

function RootTopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const { email } = useAuthStore();
  const { logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-10 flex h-14 items-center px-4 shadow-sm" style={{ backgroundColor: '#10032A', borderBottom: '1px solid #281C3F' }}>
      <button
        onClick={onMenuClick}
        className="mr-3 rounded-md p-1.5 lg:hidden"
        style={{ color: '#584F6A' }}
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="flex items-center gap-2">
        <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: 14, color: '#FFFFFF' }} className="hidden sm:block">
          Bread List Sites
        </span>
      </div>

      <div className="flex-1" />

      <div className="flex items-center gap-3">
        {/* Superadmin badge */}
        <span
          className="hidden sm:inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold"
          style={{ backgroundColor: '#4E1F60', color: '#C9B8F4', fontFamily: 'Montserrat, sans-serif', fontWeight: 600 }}
        >
          Superadmin
        </span>

        {/* Notifications */}
        <button className="rounded-md p-1.5" style={{ color: '#584F6A' }} aria-label="Notifications">
          <Bell className="h-5 w-5" />
        </button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <div
                className="flex h-7 w-7 items-center justify-center rounded-full text-sm font-medium"
                style={{ backgroundColor: '#4E1F60', color: '#C9B8F4', fontFamily: 'Montserrat, sans-serif' }}
              >
                {email ? email[0].toUpperCase() : 'U'}
              </div>
              <ChevronDown className="h-4 w-4" style={{ color: '#584F6A' }} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate('/account?tab=security')}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              Sign-in security
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-brand-error">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

// ── Layout ─────────────────────────────────────────────────────────────────────

export function RootLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-[calc(100vh-var(--impersonation-bar))]" style={{ backgroundColor: '#F8F6FF' }} data-shell="root">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 lg:hidden"
          style={{ backgroundColor: 'rgba(16,3,42,0.5)' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 w-64 transform transition-transform duration-200 ease-in-out',
          'lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <RootSidebar onClose={() => setSidebarOpen(false)} />
      </aside>

      <main className="flex-1 overflow-auto">
        <RootTopBar onMenuClick={() => setSidebarOpen(true)} />
        <Outlet />
      </main>
    </div>
  );
}
